import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Lightbulb, AlertTriangle, Bug, X } from 'lucide-react';
import { api } from '../services/api';

type FormType = 'feature' | 'brokenSite' | 'bug' | null;

export function HelpMenu() {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeForm, setActiveForm] = useState<FormType>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Feature request form state
  const [featureTitle, setFeatureTitle] = useState('');
  const [featureDesc, setFeatureDesc] = useState('');

  // Broken site form state
  const [siteUrl, setSiteUrl] = useState('');
  const [whatsBroken, setWhatsBroken] = useState('');

  // Bug report form state
  const [whatHappened, setWhatHappened] = useState('');
  const [stepsToReproduce, setStepsToReproduce] = useState('');

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  const resetForms = useCallback(() => {
    setFeatureTitle('');
    setFeatureDesc('');
    setSiteUrl('');
    setWhatsBroken('');
    setWhatHappened('');
    setStepsToReproduce('');
  }, []);

  const closeModal = useCallback(() => {
    setActiveForm(null);
    setShowThankYou(false);
    resetForms();
  }, [resetForms]);

  // Close modal on Escape
  useEffect(() => {
    if (!activeForm && !showThankYou) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activeForm, showThankYou, closeModal]);

  const openForm = (form: FormType) => {
    setMenuOpen(false);
    setActiveForm(form);
  };

  const submitFeatureRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      await api.submitFeedback(
        'feature',
        `Feature Request: ${featureTitle}`,
        `Feature: ${featureTitle}\n\nDescription:\n${featureDesc}`,
      );
      setShowThankYou(true);
      setActiveForm(null);
      resetForms();
    } catch {
      setSubmitError(t('app.help.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitBrokenSite = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    let domain = siteUrl;
    try {
      domain = new URL(siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`).hostname;
    } catch {
      // Use raw input if URL parsing fails
    }
    try {
      await api.submitFeedback(
        'broken-site',
        `Site Issue: ${domain}`,
        `Site URL: ${siteUrl}\n\nWhat's broken:\n${whatsBroken}`,
      );
      setShowThankYou(true);
      setActiveForm(null);
      resetForms();
    } catch {
      setSubmitError(t('app.help.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitBugReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    const shortDesc = whatHappened.slice(0, 50);
    try {
      await api.submitFeedback(
        'bug',
        `Bug Report: ${shortDesc}`,
        `What happened:\n${whatHappened}${stepsToReproduce ? `\n\nSteps to reproduce:\n${stepsToReproduce}` : ''}`,
      );
      setShowThankYou(true);
      setActiveForm(null);
      resetForms();
    } catch {
      setSubmitError(t('app.help.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Help FAB */}
      <div ref={menuRef}>
        <button
          className="help-button"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label={t('app.help.button')}
        >
          <HelpCircle size={24} />
        </button>

        {/* Menu popover */}
        {menuOpen && (
          <div className="help-menu">
            <button className="help-menu-item" onClick={() => openForm('feature')}>
              <Lightbulb size={20} className="help-menu-item-icon" />
              {t('app.help.featureRequest')}
            </button>
            <button className="help-menu-item" onClick={() => openForm('brokenSite')}>
              <AlertTriangle size={20} className="help-menu-item-icon" />
              {t('app.help.brokenSite')}
            </button>
            <button className="help-menu-item" onClick={() => openForm('bug')}>
              <Bug size={20} className="help-menu-item-icon" />
              {t('app.help.bugReport')}
            </button>
          </div>
        )}
      </div>

      {/* Feature Request Modal */}
      {activeForm === 'feature' && (
        <div className="help-modal-overlay" onClick={closeModal}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <span className="help-modal-title">{t('app.help.featureTitle')}</span>
              <button className="help-modal-close" onClick={closeModal} aria-label={t('app.help.close')}>
                <X size={20} />
              </button>
            </div>
            <form className="help-form" onSubmit={submitFeatureRequest}>
              <div>
                <label>{t('app.help.featureName')}</label>
                <input
                  className="input"
                  type="text"
                  placeholder={t('app.help.featureNamePlaceholder')}
                  value={featureTitle}
                  onChange={(e) => setFeatureTitle(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>{t('app.help.featureDesc')}</label>
                <textarea
                  className="input"
                  placeholder={t('app.help.featureDescPlaceholder')}
                  value={featureDesc}
                  onChange={(e) => setFeatureDesc(e.target.value)}
                />
              </div>
              {submitError && <p className="help-form-error">{submitError}</p>}
              <button type="submit" className="btn btn-primary" disabled={!featureTitle.trim() || submitting}>
                {submitting ? t('common.loading') : t('app.help.submit')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Broken Site Modal */}
      {activeForm === 'brokenSite' && (
        <div className="help-modal-overlay" onClick={closeModal}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <span className="help-modal-title">{t('app.help.brokenSiteTitle')}</span>
              <button className="help-modal-close" onClick={closeModal} aria-label={t('app.help.close')}>
                <X size={20} />
              </button>
            </div>
            <form className="help-form" onSubmit={submitBrokenSite}>
              <div>
                <label>{t('app.help.siteUrl')}</label>
                <input
                  className="input"
                  type="text"
                  placeholder={t('app.help.siteUrlPlaceholder')}
                  value={siteUrl}
                  onChange={(e) => setSiteUrl(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>{t('app.help.whatsBroken')}</label>
                <textarea
                  className="input"
                  placeholder={t('app.help.whatsBrokenPlaceholder')}
                  value={whatsBroken}
                  onChange={(e) => setWhatsBroken(e.target.value)}
                  required
                />
              </div>
              {submitError && <p className="help-form-error">{submitError}</p>}
              <button type="submit" className="btn btn-primary" disabled={!siteUrl.trim() || !whatsBroken.trim() || submitting}>
                {submitting ? t('common.loading') : t('app.help.submit')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Bug Report Modal */}
      {activeForm === 'bug' && (
        <div className="help-modal-overlay" onClick={closeModal}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <span className="help-modal-title">{t('app.help.bugTitle')}</span>
              <button className="help-modal-close" onClick={closeModal} aria-label={t('app.help.close')}>
                <X size={20} />
              </button>
            </div>
            <form className="help-form" onSubmit={submitBugReport}>
              <div>
                <label>{t('app.help.whatHappened')}</label>
                <textarea
                  className="input"
                  placeholder={t('app.help.whatHappenedPlaceholder')}
                  value={whatHappened}
                  onChange={(e) => setWhatHappened(e.target.value)}
                  required
                />
              </div>
              <div>
                <label>{t('app.help.stepsToReproduce')}</label>
                <textarea
                  className="input"
                  placeholder={t('app.help.stepsPlaceholder')}
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                />
              </div>
              {submitError && <p className="help-form-error">{submitError}</p>}
              <button type="submit" className="btn btn-primary" disabled={!whatHappened.trim() || submitting}>
                {submitting ? t('common.loading') : t('app.help.submit')}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Thank You Modal */}
      {showThankYou && (
        <div className="help-modal-overlay" onClick={closeModal}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-thank-you">
              <h3>{t('app.help.thankYou')}</h3>
              <p>{t('app.help.thankYouDesc')}</p>
              <button className="btn btn-primary" onClick={closeModal} style={{ marginTop: '1rem' }}>
                {t('app.help.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
